// Small in-memory cache for idempotent GETs.
//
// The API client already shares in-flight promises, which covers Strict Mode
// double-effects and concurrent mounts. It does not cover *sequential* mounts:
// navigating /tools → /tools/x → back re-issued the full tool list every time.
// These payloads are localized static content, so a short TTL is enough to
// collapse that into one request per session-ish window.

interface CacheEntry<T> {
  value: T;
  /** Epoch ms after which the entry is no longer served. */
  expiresAt: number;
}

const entries = new Map<string, CacheEntry<unknown>>();
const inflight = new Map<string, Promise<unknown>>();

/** Default freshness window for localized catalog data. */
export const CATALOG_TTL_MS = 5 * 60_000;

/**
 * Resolve `key` from cache, an in-flight request, or by running `load`.
 * Failures are never cached — the next call retries.
 */
export function cachedRequest<T>(
  key: string,
  load: () => Promise<T>,
  ttlMs: number = CATALOG_TTL_MS,
): Promise<T> {
  const hit = entries.get(key);
  if (hit && hit.expiresAt > Date.now()) {
    return Promise.resolve(hit.value as T);
  }
  if (hit) entries.delete(key);

  const pending = inflight.get(key);
  if (pending) return pending as Promise<T>;

  const promise = load()
    .then((value) => {
      entries.set(key, { value, expiresAt: Date.now() + ttlMs });
      return value;
    })
    .finally(() => {
      if (inflight.get(key) === promise) inflight.delete(key);
    });

  inflight.set(key, promise);
  return promise;
}

/** Drop cached entries whose key starts with `prefix` (all when omitted). */
export function invalidateCache(prefix?: string): void {
  if (prefix === undefined) {
    entries.clear();
    return;
  }
  for (const key of entries.keys()) {
    if (key.startsWith(prefix)) entries.delete(key);
  }
}

// ── Namespaces ───────────────────────────────────────────────────
// Key prefixes live here rather than in the individual API modules so the
// invalidation helpers below can be imported from anywhere without creating
// import cycles between `projects.ts`, `generations.ts` and `tools.ts`.

export const PROJECTS_CACHE_PREFIX = "projects:";
export const GENERATIONS_CACHE_PREFIX = "generations:";
export const COMMERCE_CACHE_PREFIX = "commerce:";

/**
 * Freshness window for user-mutable lists. Every write path invalidates
 * explicitly, so this only bounds staleness from changes made elsewhere
 * (another tab, another device, the API itself).
 */
export const LIST_TTL_MS = 60_000;

/** Drop cached GET /v1/project pages. */
export function invalidateProjects(): void {
  invalidateCache(PROJECTS_CACHE_PREFIX);
}

/** Drop cached GET /v1/generation pages. */
export function invalidateGenerations(): void {
  invalidateCache(GENERATIONS_CACHE_PREFIX);
}

/**
 * A generation was created, renamed or claimed. Project list rows embed
 * generation titles and counts, so both namespaces go stale together.
 */
export function invalidateGenerationsAndProjects(): void {
  invalidateGenerations();
  invalidateProjects();
}
